import {
  Box,
  Card,
  CardMedia,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@material-ui/core";
import StarIcon from "@material-ui/icons/Star";
import StarBorderIcon from "@material-ui/icons/StarBorder";
import DownloadIcon from "@mui/icons-material/Download";
import ShareIcon from "@mui/icons-material/Share";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import themeColorsDesingSystem from "assets/theme/ds-colors";
import { getTypeLabel, formatPeriodo, getFavoriteKey } from "../utils";
import useMarketingStyles from "../styles";

const { primary, green, gray, text } = themeColorsDesingSystem;

function CardActionButtons({
  classes,
  item,
  onView,
  onShare,
  onDownload,
  stopPropagation = false,
}) {
  const wrap = (handler) => (event) => {
    if (stopPropagation) event.stopPropagation();
    handler(item);
  };

  return (
    <Box className={classes.cardActions}>
      <Tooltip title="Visualizar">
        <IconButton size="small" onClick={wrap(onView)} aria-label="Visualizar">
          <VisibilityIcon fontSize="small" style={{ color: gray.dark }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Compartilhar">
        <IconButton
          size="small"
          onClick={wrap(onShare)}
          aria-label="Compartilhar"
        >
          <ShareIcon fontSize="small" style={{ color: gray.dark }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Download">
        <IconButton
          size="small"
          onClick={wrap(onDownload)}
          aria-label="Download"
        >
          <DownloadIcon fontSize="small" style={{ color: gray.dark }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

export default function MarketingCard({
  item,
  variant = "media",
  compact = false,
  isFavorite,
  onView,
  onDownload,
  onShare,
  onFavorite,
}) {
  const classes = useMarketingStyles();

  if (variant === "file") {
    return (
      <Box className={classes.fileCard} onClick={() => onView(item)}>
        <Box
          style={{
            width: 48,
            height: 48,
            borderRadius: 8,
            backgroundColor: `${primary.main}14`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginRight: 14,
            flexShrink: 0,
          }}
        >
          <PictureAsPdfIcon style={{ fontSize: 28, color: primary.main }} />
        </Box>
        <Box flex={1} minWidth={0}>
          <Typography
            variant="subtitle2"
            style={{ fontWeight: 600, color: text.primary, marginBottom: 2 }}
            noWrap
          >
            {item.titulo}
          </Typography>
          <Typography variant="caption" style={{ color: gray.dark }}>
            {getTypeLabel(item.tipo)} · {item.tamanho || "PDF"}
          </Typography>
        </Box>
        <CardActionButtons
          classes={classes}
          item={item}
          onView={onView}
          onShare={onShare}
          onDownload={onDownload}
          stopPropagation
        />
      </Box>
    );
  }

  const isVideo = item.tipo === "Video";

  return (
    <Card className={classes.contentCard} elevation={0}>
      <Box
        position="relative"
        onClick={() => onView(item)}
        style={{ cursor: "pointer" }}
      >
        <CardMedia
          component="img"
          image={item.thumbnail}
          alt={item.titulo}
          className={`${classes.cardMedia} ${compact ? classes.cardMediaCompact : ""}`}
        />
        {isVideo && (
          <Box
            position="absolute"
            top="50%"
            left="50%"
            style={{
              transform: "translate(-50%, -50%)",
              backgroundColor: "rgba(255,255,255,0.92)",
              borderRadius: "50%",
              width: 52,
              height: 52,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PlayCircleOutlineIcon
              style={{ fontSize: 36, color: primary.main }}
            />
          </Box>
        )}
        {item.isNovo && (
          <Chip
            label="Novo"
            size="small"
            style={{
              position: "absolute",
              top: 10,
              left: 10,
              backgroundColor: green.main,
              color: "#fff",
              fontWeight: 600,
              height: 24,
            }}
          />
        )}
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onFavorite(item);
          }}
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            backgroundColor: "rgba(255,255,255,0.92)",
            padding: 6,
          }}
          aria-label="Favoritar"
        >
          {isFavorite ? (
            <StarIcon fontSize="small" style={{ color: primary.main }} />
          ) : (
            <StarBorderIcon fontSize="small" style={{ color: gray.dark }} />
          )}
        </IconButton>
      </Box>

      <Box className={classes.cardFooter}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box flex={1} minWidth={0} paddingRight={1}>
            <Typography
              variant="subtitle2"
              style={{
                fontWeight: 700,
                color: text.primary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {item.titulo}
            </Typography>
            <Typography
              variant="caption"
              style={{ color: gray.dark, display: "block", marginTop: 2 }}
            >
              {getTypeLabel(item.tipo)}
            </Typography>
            {!compact && (
              <Typography
                variant="caption"
                style={{ color: gray.main, display: "block", marginTop: 2 }}
              >
                {formatPeriodo(item.dataInicio, item.dataFim)}
              </Typography>
            )}
          </Box>
          <CardActionButtons
            classes={classes}
            item={item}
            onView={onView}
            onShare={onShare}
            onDownload={onDownload}
          />
        </Box>
      </Box>
    </Card>
  );
}

export function MarketingGrid({
  items,
  variant = "media",
  compact = false,
  horizontal = false,
  gridProps = { xs: 12, sm: 6, md: 4 },
  cardHandlers,
}) {
  const classes = useMarketingStyles();

  if (!items.length) return null;

  const renderCard = (item) => (
    <MarketingCard
      key={getFavoriteKey(item)}
      item={item}
      variant={variant}
      compact={compact}
      isFavorite={cardHandlers.isFavorite(item)}
      onView={cardHandlers.onView}
      onDownload={cardHandlers.onDownload}
      onShare={cardHandlers.onShare}
      onFavorite={cardHandlers.onFavorite}
    />
  );

  if (horizontal) {
    return <Box className={classes.videoScroll}>{items.map(renderCard)}</Box>;
  }

  return (
    <Grid container spacing={3}>
      {items.map((item) => (
        <Grid item {...gridProps} key={getFavoriteKey(item)}>
          {renderCard(item)}
        </Grid>
      ))}
    </Grid>
  );
}
